import "./styles.scss";
import { useHistory } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  getAppConfigListAPI,
  setAppConfigAPI,
  deleteAppConfigAPI,
  editAppAPI,
  getMoreAppsSelectionAPI,
  setMoreAppsSelectionAPI,
} from "../api/ClientApi";
import { useDispatch } from "react-redux";
import { handleLogoutRedux } from "../../redux/actions/userAction";
import { Confirm } from "react-admin";
import {
  SettingsIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  AppsIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ExternalLinkIcon,
} from "../icons";

const emptyForm = { key: "", value: "" };

const AppConfig = (props) => {
  const [appId, setAppId] = useState("");
  const [configs, setConfigs] = useState([]);
  const [reload, setReload] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Đủ field bắt buộc của /edit-app (appName/bundleId/os) — cần để gọi lại
  // editAppAPI khi đổi công tắc tổng "show_in_more_apps", vì field đó nằm trên
  // App, không phải AppConfig. Lấy sẵn từ props.location.state (item đầy đủ từ
  // list-app), không cần gọi API riêng.
  const [appMeta, setAppMeta] = useState(null);
  const [showInMoreApps, setShowInMoreApps] = useState(true);
  const [savingShowInMoreApps, setSavingShowInMoreApps] = useState(false);
  // Mỗi phần tử: { app_id, app_name, os, selected, more_apps_order }.
  const [moreAppsSelection, setMoreAppsSelection] = useState([]);
  const [loadingSelection, setLoadingSelection] = useState(true);
  const [savingSelection, setSavingSelection] = useState(false);
  // Nguồn dữ liệu thật cho thứ tự trả về trong more_apps: mảng app_id theo
  // đúng thứ tự sẽ gửi lên `promotedAppIds` (BE lưu vào sort_order theo index).
  // Tách riêng khỏi moreAppsSelection (list checkbox vẫn giữ thứ tự A-Z cho dễ
  // tìm) để có "vùng hiển thị thứ tự" riêng, sắp lại được bằng nút lên/xuống.
  const [selectedOrder, setSelectedOrder] = useState([]);
  // Snapshot của selectedOrder tại thời điểm load xong / lưu thành công lần
  // gần nhất — so sánh với selectedOrder hiện tại để biết có đang dirty hay
  // không, dùng để enable/disable nút Lưu.
  const [savedOrder, setSavedOrder] = useState([]);
  // icon_url có thể trỏ tới ảnh không tải được (404, domain sai...) — ẩn <img>
  // và rơi về chữ cái đầu tên app khi load lỗi, thay vì để hiện icon ảnh vỡ.
  const [avatarFailed, setAvatarFailed] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    const app = props.location.state;
    if (!app) {
      history.push("/admin");
      return;
    }
    setAppId(app.app_id);
    setAppMeta({
      appName: app.app_name,
      bundleId: app.bundle_id,
      os: app.os,
      appUrl: app.app_url,
      iconUrl: app.icon_url,
    });
    // Backend trả boolean dạng 0/1 (số) — so sánh !== 0, không phải !== false
    // (strict-equality giữa number và boolean luôn true).
    setShowInMoreApps(app.show_in_more_apps !== 0);

    getMoreAppsSelectionAPI({ appId: app.app_id })
      .then((response) => {
        const { errorCode, message, data } = response;
        if (errorCode === 0) {
          setMoreAppsSelection(data);
          const initialOrder = data
            .filter((a) => a.selected)
            .sort((a, b) => a.more_apps_order - b.more_apps_order)
            .map((a) => a.app_id);
          setSelectedOrder(initialOrder);
          setSavedOrder(initialOrder);
        } else if (errorCode === 401) {
          toast.error(message);
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
        }
      })
      .finally(() => setLoadingSelection(false));
  }, []);

  const handleToggleShowInMoreApps = (checked) => {
    setShowInMoreApps(checked);
    setSavingShowInMoreApps(true);
    editAppAPI({
      appId,
      appName: appMeta.appName,
      bundleId: appMeta.bundleId,
      os: appMeta.os,
      appUrl: appMeta.appUrl,
      showInMoreApps: checked,
    })
      .then((response) => {
        const { errorCode, message } = response;
        if (errorCode === 0) {
          toast.success(message);
        } else if (errorCode === 401) {
          toast.error(message);
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
          setShowInMoreApps(!checked); // revert nếu lưu thất bại
        }
      })
      .finally(() => setSavingShowInMoreApps(false));
  };

  const toggleMoreAppSelected = (targetAppId) => {
    setMoreAppsSelection((prev) =>
      prev.map((a) => (a.app_id === targetAppId ? { ...a, selected: !a.selected } : a))
    );
    setSelectedOrder((prev) =>
      // Tick: thêm vào cuối (thứ tự cuối cùng). Bỏ tick: loại khỏi danh sách,
      // các app sau tự lùi thứ tự (đánh số lại bằng index khi render).
      prev.includes(targetAppId) ? prev.filter((id) => id !== targetAppId) : [...prev, targetAppId]
    );
  };

  // direction: -1 (lên) hoặc 1 (xuống).
  const moveSelectedOrder = (targetAppId, direction) => {
    setSelectedOrder((prev) => {
      const index = prev.indexOf(targetAppId);
      const swapWith = index + direction;
      if (index === -1 || swapWith < 0 || swapWith >= prev.length) {
        return prev;
      }
      const next = [...prev];
      [next[index], next[swapWith]] = [next[swapWith], next[index]];
      return next;
    });
  };

  const isSelectionDirty = JSON.stringify(selectedOrder) !== JSON.stringify(savedOrder);

  const handleSaveMoreAppsSelection = () => {
    setSavingSelection(true);
    setMoreAppsSelectionAPI({ appId, promotedAppIds: selectedOrder })
      .then((response) => {
        const { errorCode, message } = response;
        if (errorCode === 0) {
          toast.success(message);
          setSavedOrder(selectedOrder);
        } else if (errorCode === 401) {
          toast.error(message);
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
        }
      })
      .finally(() => setSavingSelection(false));
  };

  useEffect(() => {
    if (reload && appId) {
      getAppConfigListAPI({ appId }).then((response) => {
        setReload(false);
        const { errorCode, message, data } = response;
        if (errorCode === 0) {
          setConfigs(data);
        } else if (errorCode === 401) {
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
        }
      });
    }
  }, [reload, appId]);

  const containsWhitespace = (str) => /\s/.test(str);

  const invalidInputs = () => {
    if (!form.key) {
      toast.error("Key is not empty!");
      return false;
    }
    if (containsWhitespace(form.key)) {
      toast.error("Key must not contain whitespace!");
      return false;
    }
    if (!form.value) {
      toast.error("Value is not empty!");
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setForm(emptyForm);
    setIsEditing(false);
  };

  const handleSave = () => {
    if (!invalidInputs()) {
      return;
    }
    setSaving(true);
    setAppConfigAPI({ appId, key: form.key, value: form.value })
      .then((response) => {
        const { errorCode, message } = response;
        if (errorCode === 0) {
          toast.success(message);
          resetForm();
          setReload(true);
        } else if (errorCode === 401) {
          toast.error(message);
          dispatch(handleLogoutRedux());
        } else {
          toast.error(message);
        }
      })
      .finally(() => setSaving(false));
  };

  const handleEdit = (item) => {
    setIsEditing(true);
    setForm({ key: item.config_key, value: item.config_value || "" });
  };

  const handleDelete = () => {
    if (!deleteTarget) {
      return;
    }
    const key = deleteTarget;
    setDeleteTarget(null);
    deleteAppConfigAPI({ appId, key }).then((response) => {
      const { errorCode, message } = response;
      if (errorCode === 0) {
        toast.success(message);
        if (isEditing && form.key === key) {
          resetForm();
        }
        setReload(true);
      } else if (errorCode === 401) {
        toast.error(message);
        dispatch(handleLogoutRedux());
      } else {
        toast.error(message);
      }
    });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="d-flex align-items-center gap-3">
          <div className="app-config-avatar">
            {appMeta && appMeta.iconUrl && !avatarFailed ? (
              <img src={appMeta.iconUrl} alt="" onError={() => setAvatarFailed(true)} />
            ) : (
              (appMeta ? appMeta.appName : appId).charAt(0).toUpperCase()
            )}
          </div>
          <div>
            <h1 className="page-title d-flex align-items-center gap-2">
              {appMeta ? appMeta.appName : appId}
              {appMeta && (
                <span className="badge rounded-pill text-bg-secondary">{appMeta.os}</span>
              )}
            </h1>
            <p className="page-subtitle mb-0">
              {appId}
              {appMeta && appMeta.bundleId && appMeta.bundleId !== appId && (
                <> · bundle id: {appMeta.bundleId}</>
              )}
              {appMeta && appMeta.appUrl && (
                <>
                  {" · "}
                  <a
                    className="app-config-store-link"
                    href={appMeta.appUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Xem trên store <ExternalLinkIcon width={12} height={12} />
                  </a>
                </>
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => history.push("/admin")}
        >
          ← Quay lại App Manager
        </button>
      </div>

      <div className="card shadow-sm push-form-card mb-4">
        <div className="card-body">
          <h5 className="mb-1 d-flex align-items-center gap-2">
            <AppsIcon width={18} height={18} />
            More Apps (giới thiệu chéo)
          </h5>
          <p className="text-muted small mb-3">
            Danh sách app khác hiện trong màn "More Apps" phía app di động — trả kèm trong
            cùng response <code>/api/v1/app-config</code> (field <code>more_apps</code>), app chỉ
            cần gọi 1 API.
          </p>
          <div className="form-check mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="showInMoreApps"
              checked={showInMoreApps}
              disabled={savingShowInMoreApps}
              onChange={(event) => handleToggleShowInMoreApps(event.target.checked)}
            />
            <label className="form-check-label" htmlFor="showInMoreApps">
              Hiển thị app này trong "More Apps" của các app khác (công tắc tổng — tắt thì ẩn
              hẳn, kể cả app nào đã chọn hiện app này)
            </label>
          </div>

          <label className="form-label">
            App khác hiện trong màn "More Apps" của <strong>{appId}</strong>
          </label>
          {loadingSelection ? (
            <div className="text-muted small">Đang tải danh sách app...</div>
          ) : moreAppsSelection.length === 0 ? (
            <div className="text-muted small">Chưa có app nào khác trong hệ thống.</div>
          ) : (
            <div className="more-app-list border rounded p-1 mb-3">
              {moreAppsSelection.map((item) => (
                <div className="more-app-row" key={item.app_id}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`moreApp-${item.app_id}`}
                    checked={item.selected}
                    onChange={() => toggleMoreAppSelected(item.app_id)}
                  />
                  <label className="form-check-label" htmlFor={`moreApp-${item.app_id}`}>
                    {item.app_name}{" "}
                    <span className="text-muted small">({item.app_id})</span>
                  </label>
                  <span className="badge rounded-pill text-bg-light border more-app-os-badge">
                    {item.os}
                  </span>
                </div>
              ))}
            </div>
          )}

          {selectedOrder.length > 0 && (
            <div className="mb-3">
              <label className="form-label mb-2">
                Thứ tự trả về trong <code>more_apps</code>
              </label>
              <div className="more-app-order-list">
                {selectedOrder.map((id, index) => {
                  const app = moreAppsSelection.find((a) => a.app_id === id);
                  return (
                    <div className="more-app-order-row" key={id}>
                      <span className="more-app-order-badge">{index + 1}</span>
                      <span className="more-app-order-name">{app ? app.app_name : id}</span>
                      <div className="d-flex gap-1">
                        <button
                          type="button"
                          className="icon-btn"
                          title="Lên"
                          disabled={index === 0}
                          onClick={() => moveSelectedOrder(id, -1)}
                        >
                          <ArrowUpIcon width={14} height={14} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          title="Xuống"
                          disabled={index === selectedOrder.length - 1}
                          onClick={() => moveSelectedOrder(id, 1)}
                        >
                          <ArrowDownIcon width={14} height={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={loadingSelection || savingSelection || !isSelectionDirty}
              onClick={handleSaveMoreAppsSelection}
            >
              {savingSelection ? "Đang lưu..." : "Lưu danh sách More Apps"}
            </button>
            {isSelectionDirty && !savingSelection && (
              <span className="dirty-hint">
                <span className="dirty-hint-dot" /> Có thay đổi chưa lưu
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="card shadow-sm push-form-card mb-4">
        <div className="card-body">
          <h5 className="mb-1 d-flex align-items-center gap-2">
            <SettingsIcon width={18} height={18} />
            {isEditing ? "Cập nhật config" : "Thêm config mới"}
          </h5>
          <p className="text-muted small mb-3">
            Giá trị được mã hoá khi lưu vào DB và khi trả về cho app, nhưng hiển thị dạng gốc
            ở đây để tiện quản lý.
          </p>
          <div className="row">
            <div className="col-md-4 mb-3">
              <label className="form-label">Key</label>
              <input
                className="form-control"
                type="text"
                placeholder="VD: api_secret"
                value={form.key}
                disabled={isEditing}
                onChange={(event) => setForm({ ...form, key: event.target.value })}
              />
            </div>
            <div className="col-md-8 mb-3">
              <label className="form-label">Value</label>
              <input
                className="form-control"
                type="text"
                placeholder="Giá trị config"
                value={form.value}
                onChange={(event) => setForm({ ...form, value: event.target.value })}
              />
            </div>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              disabled={saving}
              onClick={handleSave}
            >
              <PlusIcon width={16} height={16} />
              {saving ? "Đang lưu..." : isEditing ? "Cập nhật" : "Thêm config"}
            </button>
            {isEditing && (
              <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                Huỷ
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead>
              <tr>
                <th scope="col">Key</th>
                <th scope="col">Value</th>
                <th scope="col">Cập nhật lúc</th>
                <th scope="col">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((item, index) => {
                return (
                  <tr key={index}>
                    <td>
                      <code>{item.config_key}</code>
                    </td>
                    <td>
                      <div className="text-truncate" style={{ maxWidth: 320 }}>
                        <code>{item.config_value ?? "—"}</code>
                      </div>
                    </td>
                    <td>{new Date(item.updatedAt).toLocaleString()}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="icon-btn"
                          title="Edit config"
                          onClick={() => handleEdit(item)}
                        >
                          <PencilIcon width={16} height={16} />
                        </button>
                        <button
                          type="button"
                          className="icon-btn"
                          title="Delete config"
                          onClick={() => setDeleteTarget(item.config_key)}
                        >
                          <TrashIcon width={16} height={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {configs.length === 0 && (
          <div className="empty-state">Chưa có config nào cho app này.</div>
        )}
      </div>

      <Confirm
        isOpen={!!deleteTarget}
        loading={false}
        title=""
        content={`Xác nhận xoá config "${deleteTarget}"? Hành động này không thể hoàn tác.`}
        confirm="Xoá"
        confirmColor="warning"
        cancel="Huỷ bỏ"
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default AppConfig;
